// =============================================================================
// rank-admin-client.ts — Client API ADMIN du mode « Référencement / Rang » (S17-bis / D2), DOUBLABLE.
// =============================================================================
// Miroir HTTP des endpoints ADMIN exposés par `referencement-api`
// (apps/api/src/analytics/positioning-admin.controller.ts — routes GELÉES D1) du tableau de travail
// Rang. ÉCRAN ADMIN (cookie de session admin + double-submit `admin_csrf` sur les mutations).
//
// SÉPARATION STRICTE (décision GATE_0) : ce client est l'accès ADMIN au tableau de travail Rang et à
// ses commandes (poser / retirer / figer). Il NE remplace PAS le `PositioningClient` FOURNISSEUR
// (positioning-client.ts, session-fournisseur, indicateur F-10) : ce sont deux concerns DISTINCTS sur
// deux sessions distinctes. Parité d'un client admin existant (legal-admin-client / campaign-client).
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, RBAC AdminFull). Le front ne porte AUCUNE règle métier :
//   • le grain du tableau est le TRINÔME (campaign × siret × profil × niveau) — composé par l'API ;
//   • le rang s'écrit au grain FOURNISSEUR×PROFIL (G0-D1-8) : l'API DÉNORMALISE le tag sur les niveaux ;
//   • « Figer le panel » est RELAYÉ (status `relayed`) — D1 publie une demande, D3 fige (G0-D1-1).
//   • Les compteurs A/B/PROSPECT/non-retenus et la vue nominative sont DÉRIVÉS côté front à partir du
//     read-model nominatif (vue admin §2.6, AUCUN K-anon — la K-anonymity ne protège que Pulse).
//
// DTO = miroirs HTTP des contrats GELÉS D1a/D1c/D1d (PAS @jeko/db, PAS le Published Language ; le
// contrat D1a vit dans apps/api, non publié dans @jeko/contracts — on en RÉPLIQUE la forme readonly,
// parité legal-admin-client / positioning-client). Le miroir est figé sur le contrat validé John (D1a) :
// toute dérive d'un champ est une régression de contrat. Aucune donnée métier en localStorage (RULE_REF_006).

import { HttpApiError } from '@/api/http-error';

/**
 * Échelle de rang FERMÉE {A, B, PROSPECT, NonReferenced} (codes ASCII, miroir D1a RankCode, DBU §3.7.6).
 * `NonReferenced` = aucun tag posé (valeur par défaut §2.6) ; il N'est PAS assignable (cf. AssignableRankDto).
 */
export type RankCode = 'A' | 'B' | 'PROSPECT' | 'NonReferenced';

/**
 * Une ligne du tableau de travail Rang, au grain TRINÔME (campaign × supplier_siret × profile_code ×
 * seniority_level) — miroir EXACT du contrat GELÉ D1a `RankWorkingRowDto`. Métriques `null` ⇒ le
 * fournisseur n'a PAS de réponse sur ce niveau : le front affiche un TIRET, JAMAIS « 0 € » (aucun 0 inventé).
 */
export interface RankWorkingRowDto {
  /** SIRET 14 du fournisseur participant (établissement, JAMAIS SIREN). */
  readonly supplierSiret: string;
  /** Raison sociale légale (via ACL supplier-reference). `''` si l'établissement est introuvable côté ACL. */
  readonly raisonSociale: string;
  readonly profileCode: string;
  readonly seniorityLevel: string;
  /**
   * Tag de rang du couple fournisseur×profil (G0-D1-8), PARTAGÉ (dénormalisé) sur tous les niveaux du
   * triplet. `'NonReferenced'` si aucun tag n'est posé (valeur par défaut §2.6).
   */
  readonly rank: RankCode;
  /** TJM retenu (€) du NIVEAU (Cycle 2 si présent, sinon Cycle 1). `null` ⇒ pas de réponse (front : tiret). */
  readonly tjmRetenuEur: number | null;
  /** Écart à la médiane winsorisée P50 du couple profil×niveau, en %. `null` si tjm null ou pas de P50. */
  readonly ecartP50Pct: number | null;
  /** Percentile de positionnement (0..100), chiffre approché d'aide à la décision. `null` si tjm null ou pas de stats. */
  readonly percentile: number | null;
  // ───────────────────────────────────────────────────────────────────────────────────────────────
  // A1 — FUSION « Analyse + Référencement/Rang » (GATE_HUMAN 2026-09-17). Miroir des champs ADDITIFS du
  // contrat API : tous OPTIONNELS et NULLABLES (un double qui ne les pose pas reste valide). La page
  // fusionnée (lot U1) les AFFICHE et TRIE dessus ; elle ne les recalcule JAMAIS (autorité = API).
  // ───────────────────────────────────────────────────────────────────────────────────────────────
  /** Colonne « TJM C1 » (€). `null` ⇒ aucune réponse au Cycle 1 (tiret, JAMAIS 0 €). */
  readonly responseC1Eur?: number | null;
  /**
   * Colonne « TJM C2 » (€). `null` ⇒ campagne MONO-CYCLE ou aucune réponse C2 : l'UI affiche un tiret et
   * NE RECOPIE JAMAIS le C1 (« pas de C2 » et « C2 = C1 » sont deux faits distincts).
   */
  readonly responseC2Eur?: number | null;
  /**
   * Effectif du couple (profil × niveau) retenu par l'analyse. Sert à distinguer un tiret « masqué par la
   * K-anonymity » (effectif sous le seuil) d'un tiret « pas de réponse » — GATE_HUMAN 2026-09-17 : « je
   * confirme que les tirets sont les données masquées suite au K-anonimity ». `null` ⇒ couple sans stats.
   */
  readonly coupleEffectiveCount?: number | null;
  /**
   * Prix cible (€) du couple, disponible DÈS la création de la campagne (il ne vient PAS de
   * `target-price-summary`, post-clôture). `null` ⇒ aucun prix cible saisi sur ce couple.
   */
  readonly targetPriceEur?: number | null;
  /**
   * Colonne « Écart prix cible » (%) : (tjm - cible) / cible * 100, calculé PAR L'API (symétrie avec
   * `ecartP50Pct`). `null` si pas de TJM, pas de prix cible, ou cible ≤ 0. Support du tri A/D (spec §6).
   */
  readonly ecartTargetPricePct?: number | null;
}

/** Réponse du tableau de travail Rang d'une campagne (admin-only, nominatif §2.6). Miroir D1a. */
export interface RankWorkingTableDto {
  readonly campaignId: string;
  readonly rows: readonly RankWorkingRowDto[];
}

// --- R25-COMPARE-CLIENT-EVOLUTION (PHASE R) : évolution NOMINATIVE du prix retenu par client ------------
// Miroir EXACT du contrat API `rank-client-evolution.dto.ts` (apps/api). Vue ADMIN NOMINATIVE, SANS K-anon
// (surface DISTINCTE du panneau de comparaison ANONYME — C3). Indicateur = TJM retenu (DEC-15/16) ; cellule
// multi-retenu ⇒ MÉDIANE fournisseur-AGNOSTIQUE (DEC-16, C1/C4/C6).

/**
 * Une cellule de la série au grain COUPLE (profil × niveau) POUR UNE campagne figée — miroir
 * `RankClientEvolutionCellDto`. Valeur = MÉDIANE (arrondie) des TJM retenus (DEC-16).
 */
export interface RankClientEvolutionCellDto {
  readonly profileCode: string;
  readonly seniorityLevel: string;
  /** Médiane (arrondie) des TJM retenus. `null` si aucun contributeur (front : tiret, JAMAIS 0 €). */
  readonly tjmRetenuMedianEur: number | null;
  /** Nombre de fournisseurs RETENUS contribuant à la médiane (≥ 1 si tjm non-null). */
  readonly retainedCount: number;
  /** Raison sociale NOMINATIVE si un SEUL fournisseur (retainedCount === 1) ; `''` si agrégat multi-retenu. */
  readonly raisonSociale: string;
}

/** Le bloc d'évolution d'UNE campagne — miroir `RankClientEvolutionCampaignDto`. */
export interface RankClientEvolutionCampaignDto {
  readonly campaignId: string;
  /** Instant du figement (ISO 8601) — clé chronologique. `null` ⇒ campagne non figée (exclue de la série). */
  readonly frozenAt: string | null;
  readonly cells: readonly RankClientEvolutionCellDto[];
}

/** Réponse d'évolution nominative (admin-only) — miroir `RankClientEvolutionResponseDto`. */
export interface RankClientEvolutionResponseDto {
  readonly campaigns: readonly RankClientEvolutionCampaignDto[];
}

/** Rang ASSIGNABLE par une commande POSER (NonReferenced exclu — il résulte d'un RETRAIT, route dédiée). */
export type AssignableRankDto = 'A' | 'B' | 'PROSPECT';

/** Corps de la commande POSER un rang (le campaignId vient de l'URL, server-set). Miroir D1c. */
export interface AssignRankBodyDto {
  /** SIRET 14 du fournisseur participant (établissement, JAMAIS SIREN). */
  readonly supplierSiret: string;
  readonly profileCode: string;
  /** Rang à poser ∈ {A, B, PROSPECT}. */
  readonly rank: AssignableRankDto;
}

/** Corps de la commande RETIRER un rang (UPDATE → NonReferenced ; le campaignId vient de l'URL). Miroir D1c. */
export interface RemoveRankBodyDto {
  readonly supplierSiret: string;
  readonly profileCode: string;
}

/** Réponse de POSER : identité du tag persisté + rang appliqué. Miroir D1c. */
export interface AssignRankResponseDto {
  readonly rankingId: string;
  readonly rank: AssignableRankDto;
}

/**
 * Réponse de RETIRER (idempotent) — miroir D1c :
 *   • status='removed' → un rang {A,B,PROSPECT} a été passé à NonReferenced (DE émis) ;
 *   • status='noop'    → rien à retirer (triplet absent OU déjà NonReferenced) — aucun write, aucun DE.
 */
export type RemoveRankResponseDto =
  | {
      readonly status: 'removed';
      readonly rankingId: string;
      readonly previousRank: AssignableRankDto;
    }
  | { readonly status: 'noop' };

/**
 * Réponse de « Figer le panel » (S17-bis / D1d). Le statut est `relayed` : D1 a RELAYÉ une DEMANDE de
 * figement au worker (D3) — il N'A PAS figé (G0-D1-1, Option A). Aucun versionNumber/panelSize ici :
 * ces valeurs sont l'autorité de D3 (après figement réel). Miroir D1d.
 */
export interface FreezePanelResponseDto {
  readonly status: 'relayed';
  readonly campaignId: string;
  /** Horodatage ISO-8601 de la demande relayée (= requestedAt du message INFRA). */
  readonly requestedAt: string;
}

// --- V26-DELIVERABLE-EXPOSE (TRANCHE A) : LISTE des versions figées du livrable (D1-B3) -------------------
// Miroir EXACT du contrat `RankFreezeVersionRow` (apps/api/src/analytics/ports/rank-freeze-list-store.port.ts).
// Le livrable client est un FICHIER XLSX ; ce read-model expose sa PRÉSENCE par version (`hasXlsx`). Le
// TÉLÉCHARGEMENT des octets reste DIFFÉRÉ (TRANCHE B, dépend d'un GcsReadPort analytics) : ce client
// n'expose donc PAS de méthode de download, seulement la présence par version.
//
// ⚠️ LOT A3 — `hasPdf` RETIRÉ (décision GATE_HUMAN du 2026-09-17 : « il faut garder "Figer le panel" et
// l'historique des versions figées. Il ne faut pas garder le PDF. »). Le figement, le versionnement et
// l'historique sont CONSERVÉS ; le livrable client se produit désormais depuis l'export de la page.

/**
 * Une version figée du panel (métadonnées de listing, non nominatif) — miroir EXACT de
 * `RankFreezeVersionRow` (API). Plus récente d'abord (tri serveur).
 */
export interface RankFreezeVersionRow {
  /** Numéro de version horodatée (UK campaign × version). */
  readonly versionNumber: number;
  /** Instant du figement (ISO 8601). */
  readonly frozenAt: string;
  /** Subject OIDC (uuid) de l'admin ayant figé. */
  readonly frozenBy: string;
  /** Nombre de tags retenus dans le panel figé. */
  readonly panelSize: number;
  /** Le figement a-t-il eu lieu après publication du benchmark ? */
  readonly postPublication: boolean;
  /** Le livrable XLSX est-il déposé (présence seulement, PAS les octets — téléchargement Tranche B). */
  readonly hasXlsx: boolean;
}

// --- V26-EXPORT-JOURNAL (FRONT) : enregistrement d'un export + journal des événements ---------------------
// Miroir HTTP des endpoints GELÉS D1 (base /admin/positioning) : `POST .../export/record` (journalise une
// intention d'export du benchmark, produite CÔTÉ FRONT) et `GET .../export-journal` (journal UNIFIÉ des
// événements export + figement, déjà TRIÉ occurredAt DESC par l'API). Le FICHIER exporté n'est JAMAIS transmis
// au serveur (composition front pure) : seule la MÉTADONNÉE d'export (format, anonymisation, colonnes, filtres,
// nombre de lignes) est journalisée pour l'audit (append-only, DDD §3 n°5).

/** Niveau de séniorité du filtre journalisé (enum contrat, parité Prisma RateSeniorityLevel ASCII). */
export type ExportJournalSeniorityLevel =
  | 'Debutant'
  | 'Maitrisant'
  | 'Expert'
  | 'Referent';

/** Filtres appliqués à l'affichage au moment de l'export (journalisés pour la traçabilité). */
export interface ExportJournalFiltersDto {
  readonly profileCode?: string;
  readonly seniorityLevel?: ExportJournalSeniorityLevel;
}

/**
 * Corps du POST `.../export/record` : la MÉTADONNÉE d'un export benchmark produit côté front. `selectedColumns`
 * = les colonnes RÉELLEMENT exportées (sans `raisonSociale` si `anonymized`). L'auteur (actorRef) est
 * server-set (session admin), JAMAIS porté ici. `rowCount` = nombre de lignes du fichier généré.
 */
export interface RecordExportBodyDto {
  readonly format: 'csv' | 'xlsx';
  readonly anonymized: boolean;
  readonly selectedColumns: readonly string[];
  readonly filters: ExportJournalFiltersDto;
  readonly rowCount: number;
}

/** Réponse du POST `.../export/record` : accusé d'enregistrement (append-only, aucune donnée renvoyée). */
export interface RecordExportResponseDto {
  readonly recorded: true;
}

/**
 * Une entrée du journal UNIFIÉ (miroir `JournalEntryDto` API) : un événement d'EXPORT (`kind: 'export'`) OU de
 * FIGEMENT (`kind: 'freeze'`). Les champs spécifiques à l'un des deux `kind` sont `null` pour l'autre (union
 * APLATIE, parité contrat GELÉ). Trié occurredAt DESC par l'API (le front n'a pas à re-trier).
 */
export interface JournalEntryDto {
  /** Nature de l'événement : export benchmark ou figement du panel. */
  readonly kind: 'export' | 'freeze';
  /** Type d'événement technique (nom du Domain Event / de l'action journalisée). */
  readonly eventType: string;
  /** Horodatage de l'événement (ISO 8601). */
  readonly occurredAt: string;
  /** Référence de l'auteur (subject OIDC / libellé admin), server-set. */
  readonly actorRef: string;
  // --- Champs spécifiques EXPORT (null pour un freeze) ---
  readonly format: 'csv' | 'xlsx' | null;
  readonly anonymized: boolean | null;
  readonly selectedColumns: readonly string[] | null;
  readonly filters: ExportJournalFiltersDto | null;
  readonly rowCount: number | null;
  // --- Champs spécifiques FREEZE (null pour un export) ---
  readonly versionNumber: number | null;
  readonly panelSize: number | null;
  readonly postPublication: boolean | null;
}

/** Réponse du GET `.../export-journal` : le journal UNIFIÉ (déjà trié occurredAt DESC). */
export interface ExportJournalResponseDto {
  readonly entries: readonly JournalEntryDto[];
}

// --- LANE-E-CAMPAIGN-AUDIT (FRONT) : page d'audit campagne (A0R-9) + flag irréversible (A0R-11) ----------
// Miroir HTTP de l'endpoint ADMIN `GET /admin/campaigns/:campaignId/audit` (CampaignAuditAdminController,
// BC analytics). Liste TOUTES les actions auditées portant l'id campagne (transitions d'état, exports,
// publications, figements RANG), DÉJÀ triées occurredAt DESC par l'API. Le flag `irreversible` (A0R-11)
// est DÉRIVÉ À LA LECTURE côté API (allow-list de types) : le front l'AFFICHE tel quel, sans le recalculer.
// DOCTRINE NON-NOMINATIF : l'entrée ne porte NI payload brut NI tenant_id (surface volontairement réduite).

/**
 * Une entrée de la page d'audit campagne (miroir `CampaignAuditEntryDto` API). Enveloppe traçable
 * (type d'événement, horodatage, auteur) + flag `irreversible` (A0R-11). Trié occurredAt DESC par l'API.
 */
export interface CampaignAuditEntryDto {
  /** Type d'événement audité (nom du Domain Event / de l'action journalisée). */
  readonly eventType: string;
  /** Horodatage de l'action (ISO 8601). */
  readonly occurredAt: string;
  /** Référence de l'auteur (subject OIDC / libellé admin), server-set. */
  readonly actorRef: string;
  /** L'action est-elle IRRÉVERSIBLE (A0R-11) ? Dérivé à la lecture côté API. */
  readonly irreversible: boolean;
}

/** Réponse du GET `.../:campaignId/audit` : la page d'audit campagne (déjà triée occurredAt DESC). */
export interface CampaignAuditResponseDto {
  readonly entries: readonly CampaignAuditEntryDto[];
}

/**
 * Erreur applicative renvoyée par l'API rang admin (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle
 * ne réinterprète JAMAIS la règle métier (autorité = API : disponibilité de l'analyse, état de la vue,
 * validité du rang). 404 ⇒ pas d'analyse consolidée ; 409 ⇒ vue Rang indisponible pour le figement.
 */
export class RankAdminApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'RankAdminApiError');
  }
}

/**
 * Contrat du client ADMIN du mode Rang — miroir des endpoints GELÉS D1 (PositioningAdminController).
 * Aucune règle métier ici : la composition du read-model (4 sources via ports ACL), l'upsert du tag,
 * l'idempotence du retrait et le relais du figement sont l'autorité de l'API. L'UI relaie l'intention
 * (campaignId d'URL = ressource ; supplierSiret/profileCode/rank = body ; auteur = session admin).
 */
export interface RankAdminClient {
  /**
   * GET /admin/positioning/:campaignId/rank-working-table — read-model NOMINATIF (SIRET + raison
   * sociale, SANS K-anon §2.6) au grain TRINÔME. 404 (RankAdminApiError) si aucune analyse consolidée.
   */
  getRankWorkingTable(campaignId: string): Promise<RankWorkingTableDto>;

  /**
   * GET /admin/positioning/rank-client-evolution?campaignId=…&campaignId=… (R25-COMPARE, PHASE R).
   * Évolution NOMINATIVE du PRIX RETENU (TJM retenu, DEC-15/16) sur un ensemble de campagnes FIGÉES. Le
   * back est STATELESS sur le périmètre client : le FILTRAGE SIREN + le tri chronologique sont FRONT (C5).
   * Une campagne non figée ressort avec `frozenAt: null` (le front l'exclut de la série).
   */
  getRankClientEvolution(
    campaignIds: readonly string[],
  ): Promise<RankClientEvolutionResponseDto>;

  /**
   * POST /admin/positioning/:campaignId/rank — POSER un rang {A,B,PROSPECT} sur le couple
   * fournisseur×profil (upsert, dénormalisé sur les niveaux). 404 si pas d'analyse, 400 si corps invalide.
   */
  assignRank(
    campaignId: string,
    body: AssignRankBodyDto,
  ): Promise<AssignRankResponseDto>;

  /**
   * POST /admin/positioning/:campaignId/rank/remove — RETIRER un rang (UPDATE → NonReferenced ; JAMAIS
   * DELETE). Idempotent : `{status:'noop'}` si rien à retirer. 400 si corps invalide.
   */
  removeRank(
    campaignId: string,
    body: RemoveRankBodyDto,
  ): Promise<RemoveRankResponseDto>;

  /**
   * POST /admin/positioning/:campaignId/panel/freeze — RELAYER une demande de figement au worker (D3).
   * Réponse `{status:'relayed'}` (D1 a relayé, PAS figé). 404 si pas d'analyse, 409 si vue indisponible.
   * La vue reste ÉDITABLE après relais (re-figement autorisé, pas de verrouillage).
   */
  freezePanel(campaignId: string): Promise<FreezePanelResponseDto>;

  /**
   * GET /admin/positioning/:campaignId/freezes (V26-DELIVERABLE-EXPOSE / TRANCHE A) — LISTE des versions
   * figées du livrable, plus récente d'abord. Liste VIDE si aucun figement (pas d'erreur : état normal).
   * `hasXlsx` = présence du livrable (le téléchargement des octets est Tranche B, non exposé ici).
   */
  listFreezes(campaignId: string): Promise<readonly RankFreezeVersionRow[]>;

  /**
   * POST /admin/positioning/:campaignId/export/record (V26-EXPORT-JOURNAL) — JOURNALISE la métadonnée d'un
   * export benchmark produit CÔTÉ FRONT (le fichier n'est PAS transmis). MUTATION : double-submit CSRF requis.
   * L'auteur (actorRef) est server-set (session). Append-only (audit DDD §3 n°5). Réponse `{ recorded: true }`.
   */
  recordExport(
    campaignId: string,
    body: RecordExportBodyDto,
  ): Promise<RecordExportResponseDto>;

  /**
   * GET /admin/positioning/:campaignId/export-journal (V26-EXPORT-JOURNAL) — JOURNAL UNIFIÉ des événements
   * export + figement, déjà TRIÉ occurredAt DESC par l'API (lecture, aucun CSRF). Liste VIDE si aucun
   * événement (état normal, pas d'erreur).
   */
  getExportJournal(campaignId: string): Promise<ExportJournalResponseDto>;

  /**
   * GET /admin/campaigns/:campaignId/audit (LANE-E-CAMPAIGN-AUDIT) — PAGE d'audit campagne (A0R-9) :
   * TOUTES les actions auditées portant l'id campagne, déjà TRIÉES occurredAt DESC par l'API (lecture,
   * aucun CSRF). Chaque entrée IRRÉVERSIBLE (A0R-11) porte `irreversible: true`. Liste VIDE si aucune
   * action auditée (état normal, pas d'erreur). Aucune donnée nominative (ni payload, ni tenant).
   */
  getCampaignAudit(campaignId: string): Promise<CampaignAuditResponseDto>;
}
